import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { projects } from '../data/projects';
import { trackProjectView } from '../utils/analytics';
import OptimizedImage from './OptimizedImage';

const FEATURED_ID = 'my-nextstage';

export default function FeaturedProject() {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language as 'en' | 'de';

  const project = projects.find((p) => p.id === FEATURED_ID);
  if (!project) return null;

  const outcomeHeadline =
    project.outcomeHeadline?.[currentLang] ?? project.outcomeHeadline?.en;
  const summary = project.summary[currentLang];
  const stackDisplay =
    project.stackGrouped?.[currentLang] ??
    project.stackGrouped?.en ??
    project.stack.join(' • ');

  const blocks = [
    { label: t('projects.whatMadeItHard'), items: project.hardParts?.[currentLang] ?? project.hardParts?.en ?? [] },
    { label: t('projects.whatIShipped'), items: project.shipped?.[currentLang] ?? project.shipped?.en ?? [] },
    { label: t('projects.proofSignals'), items: project.proof?.[currentLang] ?? project.proof?.en ?? [] },
  ].filter((block) => block.items.length > 0);

  return (
    <section
      id="featured-project"
      className="section-padding section-surface-alt"
      role="region"
      aria-labelledby="featured-project-title"
    >
      <div className="container mx-auto max-w-6xl px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="glass-card shadow-elevated overflow-hidden"
        >
          <div className="h-px w-full bg-linear-to-r from-transparent via-blue-500/60 to-transparent" />
          <div className="p-6 md:p-10 lg:p-12 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14">
            <div>
              {project.imageUrl && (
                <div className="mb-6 rounded-xl overflow-hidden border border-gray-200/60 dark:border-gray-700/60 aspect-video">
                  <OptimizedImage
                    src={project.imageUrl}
                    alt={`${project.title[currentLang]} screenshot`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    width={640}
                    height={360}
                  />
                </div>
              )}
              <span className="section-label mb-3 inline-block">
                {t('home.featured.label')}
              </span>
              <h2
                id="featured-project-title"
                className="text-2xl md:text-3xl font-bold tracking-tight text-gray-900 dark:text-white mb-3"
              >
                {project.title[currentLang]}
              </h2>
              {outcomeHeadline && (
                <p className="text-lg text-gray-800 dark:text-gray-200 font-medium mb-4 leading-snug">
                  {outcomeHeadline}
                </p>
              )}
              <p className="text-base text-gray-600 dark:text-gray-400 mb-5 leading-relaxed">
                {summary}
              </p>
              <div className="flex flex-wrap items-center gap-2 mb-8">
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  {project.role[currentLang]}
                </span>
                <span className="text-gray-300 dark:text-gray-600" aria-hidden="true">·</span>
                <span className="text-xs font-mono text-gray-500 dark:text-gray-400" title={stackDisplay}>
                  {stackDisplay}
                </span>
              </div>
              <Link
                to={`/projects/${project.id}`}
                onClick={() => trackProjectView(project.id, project.title[currentLang])}
                className="button__primary"
              >
                {t('home.featured.cta')}
                <ArrowRightIcon className="w-4 h-4 ml-2" aria-hidden="true" />
              </Link>
            </div>

            <div className="space-y-6 lg:border-l lg:border-gray-200/60 dark:lg:border-gray-700/60 lg:pl-10">
              {blocks.map((block) => (
                <div key={block.label}>
                  <h3 className="text-xs font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-3">
                    {block.label}
                  </h3>
                  <ul className="space-y-2.5">
                    {block.items.map((item, i) => (
                      <li key={i} className="flex items-start text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                        <span className="shrink-0 w-1.5 h-1.5 rounded-full bg-blue-500 dark:bg-blue-400 mt-2 mr-3" aria-hidden="true" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
